import React from 'react';
import { Image, Undo2, Redo2, FlipHorizontal, FlipVertical, Trash2, Check, ZoomIn, ZoomOut, Move, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ArrowUpLeft, ArrowUpRight, ArrowDownLeft, ArrowDownRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

export interface ImageTransform {
  flipH: boolean;
  flipV: boolean;
  offsetX: number;
  offsetY: number;
  scale: number;
  aspectRatio?: number; // width / height of original image
}

interface ImageEditorToolbarProps {
  onConfirm: () => void;
  onReplace: () => void;
  onDelete: () => void;
  onFlipH: () => void;
  onFlipV: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isMobile?: boolean;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
}

export const ImageEditorToolbar: React.FC<ImageEditorToolbarProps> = ({
  onConfirm,
  onReplace,
  onDelete,
  onFlipH,
  onFlipV,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  isMobile = false,
  onZoomIn,
  onZoomOut,
}) => {
  const { t } = useLanguage();
  
  // На мобильных возвращаем null - панель уже есть под шаблоном
  if (isMobile) {
    return null;
  }
  
  return (
    <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-[100] flex flex-wrap items-center justify-center gap-1 bg-card/95 backdrop-blur-sm border border-border rounded-xl shadow-lg p-2 animate-fade-in pointer-events-auto w-auto max-w-[90vw]">
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 sm:h-8 sm:w-8 touch-manipulation"
        onClick={(e) => { e.stopPropagation(); onReplace(); }}
        title={t.replaceImage}
      >
        <Image className="w-5 h-5 sm:w-4 sm:h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 sm:h-8 sm:w-8 touch-manipulation"
        onClick={(e) => { e.stopPropagation(); onUndo(); }}
        disabled={!canUndo}
        title={t.undo}
      >
        <Undo2 className="w-5 h-5 sm:w-4 sm:h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 sm:h-8 sm:w-8 touch-manipulation"
        onClick={(e) => { e.stopPropagation(); onRedo(); }}
        disabled={!canRedo}
        title={t.redo}
      >
        <Redo2 className="w-5 h-5 sm:w-4 sm:h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 sm:h-8 sm:w-8 touch-manipulation"
        onClick={(e) => { e.stopPropagation(); onFlipH(); }}
        title={t.flipHorizontal}
      >
        <FlipHorizontal className="w-5 h-5 sm:w-4 sm:h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 sm:h-8 sm:w-8 touch-manipulation"
        onClick={(e) => { e.stopPropagation(); onFlipV(); }}
        title={t.flipVertical}
      >
        <FlipVertical className="w-5 h-5 sm:w-4 sm:h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 sm:h-8 sm:w-8 text-destructive hover:text-destructive touch-manipulation"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title={t.deleteImage}
      >
        <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
      </Button>
    </div>
  );
};

interface ImageEditorArrowsProps {
  onMove: (direction: 'up' | 'down' | 'left' | 'right' | 'up-left' | 'up-right' | 'down-left' | 'down-right') => void;
  onConfirm: () => void;
}

export const ImageEditorArrows: React.FC<ImageEditorArrowsProps> = ({
  onMove,
  onConfirm,
}) => {
  const { t } = useLanguage();
  const arrowButtonClass = "absolute p-1.5 bg-muted/80 hover:bg-muted border border-border/50 rounded-md shadow-md transition-all hover:scale-110 cursor-pointer";
  
  return (
    <>
      {/* Confirm button */}
      <button
        onClick={(e) => { e.stopPropagation(); onConfirm(); }}
        className="absolute top-2 right-2 p-2 rounded-lg bg-green-500 hover:bg-green-600 shadow-lg transition-colors z-10"
        title={t.confirm}
      >
        <Check className="w-4 h-4 text-white" />
      </button>

      {/* Top */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('up'); }}
        className={cn(arrowButtonClass, "top-2 left-1/2 -translate-x-1/2")}
        title={t.moveUp}
      >
        <ArrowUp className="w-4 h-4 text-foreground/80" />
      </button>

      {/* Bottom */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('down'); }}
        className={cn(arrowButtonClass, "bottom-2 left-1/2 -translate-x-1/2")}
        title={t.moveDown}
      >
        <ArrowDown className="w-4 h-4 text-foreground/80" />
      </button>

      {/* Left */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('left'); }}
        className={cn(arrowButtonClass, "left-2 top-1/2 -translate-y-1/2")}
        title={t.moveLeft}
      >
        <ArrowLeft className="w-4 h-4 text-foreground/80" />
      </button>

      {/* Right */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('right'); }}
        className={cn(arrowButtonClass, "right-2 top-1/2 -translate-y-1/2")}
        title={t.moveRight}
      >
        <ArrowRight className="w-4 h-4 text-foreground/80" />
      </button>

      {/* Top-Left - Scale down */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('up-left'); }}
        className={cn(arrowButtonClass, "top-2 left-2")}
        title={t.scaleDown}
      >
        <ArrowUpLeft className="w-4 h-4 text-foreground/80" />
      </button>

      {/* Top-Right - Scale up */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('up-right'); }}
        className={cn(arrowButtonClass, "top-2 right-10")}
        title={t.scaleUp}
      >
        <ArrowUpRight className="w-4 h-4 text-foreground/80" />
      </button>

      {/* Bottom-Left - Scale down */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('down-left'); }}
        className={cn(arrowButtonClass, "bottom-2 left-2")}
        title={t.scaleDown}
      >
        <ArrowDownLeft className="w-4 h-4 text-foreground/80" />
      </button>

      {/* Bottom-Right - Scale up */}
      <button
        onClick={(e) => { e.stopPropagation(); onMove('down-right'); }}
        className={cn(arrowButtonClass, "bottom-2 right-2")}
        title={t.scaleUp}
      >
        <ArrowDownRight className="w-4 h-4 text-foreground/80" />
      </button>
    </>
  );
};

export default ImageEditorToolbar;
